import { createApi } from "@reduxjs/toolkit/query/react";
import { axiosBaseQuery } from "../axiosBaseQuery";
import type { Response } from "../interfaces";
import type {
  CreatePriceDto,
  OrganizationPrice,
  UpdatePriceDto,
} from "./types";

const normalizePrices = (response: unknown): OrganizationPrice[] => {
  if (Array.isArray(response)) return response as OrganizationPrice[];
  const r = response as { data?: OrganizationPrice[] };
  return r?.data ?? [];
};

export const priceApi = createApi({
  reducerPath: "priceApi",
  baseQuery: axiosBaseQuery(),
  tagTypes: ["OrganizationPrices"],
  endpoints: (builder) => ({
    getPricesByOrganization: builder.query<OrganizationPrice[], string>({
      query: (organizationId) => ({
        url: `price/organization/${organizationId}`,
        method: "GET",
      }),
      transformResponse: normalizePrices,
      providesTags: (_result, _error, organizationId) => [
        { type: "OrganizationPrices", id: organizationId },
      ],
    }),

    createPrice: builder.mutation<Response<OrganizationPrice>, CreatePriceDto>({
      query: (data) => ({
        url: "price/create",
        method: "POST",
        data,
      }),
      invalidatesTags: (_result, _error, { organization_id }) => [
        { type: "OrganizationPrices", id: organization_id },
      ],
    }),

    updatePrice: builder.mutation<
      Response<OrganizationPrice>,
      {
        priceId: string;
        organizationId: string;
        data: UpdatePriceDto;
      }
    >({
      query: ({ priceId, data }) => ({
        url: `price/${priceId}`,
        method: "PATCH",
        data,
      }),
      async onQueryStarted(
        { priceId, organizationId, data },
        { dispatch, queryFulfilled }
      ) {
        const patchResult = dispatch(
          priceApi.util.updateQueryData(
            "getPricesByOrganization",
            organizationId,
            (draft) => {
              const price = draft.find((p) => p.price_id === priceId);
              if (!price) return;

              if (data.order_price !== undefined) price.order_price = data.order_price;
              if (data.shipping_price !== undefined) {
                price.shipping_price = data.shipping_price;
              }
              if (data.processing_fee !== undefined) {
                price.processing_fee = data.processing_fee;
              }
              if (data.gift_types !== undefined) price.gift_types = data.gift_types;
              if (data.is_default !== undefined) {
                price.is_default =
                  data.is_default === true || data.is_default === 1;
              }
              if (data.is_delete !== undefined) {
                price.is_delete =
                  data.is_delete === true || data.is_delete === 1;
              }

              if (
                data.order_price !== undefined ||
                data.shipping_price !== undefined ||
                data.processing_fee !== undefined
              ) {
                price.total_price =
                  price.order_price + price.shipping_price + price.processing_fee;
              }
            }
          )
        );

        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
      invalidatesTags: (_result, _error, { organizationId }) => [
        { type: "OrganizationPrices", id: organizationId },
      ],
    }),

  }),
});

export const {
  useGetPricesByOrganizationQuery,
  useCreatePriceMutation,
  useUpdatePriceMutation,
} = priceApi;
